import React, { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { Container as ModalContainer } from 'react-modal-promise'

import { useAppSelector, dispatch } from 'redux/store'
import { isMobileScreen } from './modules/_helpers/detectMobile'
import useWebSocketEvent, { EventTypes } from './modules/webSocket'
import Loader from '@/modules/base/components/loader/loader'
import { GlobalPortalProvider } from './hooks/useGlobalPortal'
import { MenuPopoverProvider } from './modules/layout/components/menuV2/useMenuPopover'
import ToastList from './modules/base/components/toast/toastList'
import Offline from './modules/base/components/offline'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { isMeinBuero } from '@/utils/build-env'
import { prependNews } from '@/redux/ducks/newsfeed'
import { useOpenModal } from './modules/_helpers/openModal'
import ButtonSquare, { VARIANTS } from '@/modules/base/components/buttons/square/button-square'
import lang from '@/lang'
import { IUserModel } from './modules/account/model/UserModelType'
import { WebNotificationMessage } from './modules/webSocket/constants'
import {
  PENDO_PAGELOAD_DELAY_MS,
  PENDO_READY_EVENT,
  PENDO_SCRIPT_LOADED_EVENT,
} from '@/modules/firstSteps/config'
import OnboardingGuard from '@/modules/base/components/modal/onboarding-modal/OnboardingGuard'

type Props = {
  user: IUserModel
}

const App = (props: Props) => {
  const { user } = props
  const loggedInUserId = useAppSelector(state => state.user.info.id)
  const isMobile = isMobileScreen()
  const location = useLocation()
  const pendoPageLoadRafRef = useRef<number | null>(null)
  const pendoPageLoadTimeoutRef = useRef<number | null>(null)
  const tenantRestores = useSelector(state => state.tenant.info.restores)
  const navigate = useNavigate()
  useEffect(() => {
    if (!isMeinBuero() || !tenantRestores?.length) return
    // check if we are already on the restore page with token in the url
    if (/^\/restore\/.+$/.test(window.location.pathname)) {
      return
    }
    navigate('/restore')
  }, [tenantRestores?.length])

  useWebSocketEvent(EventTypes.NOTIFICATION_NEW, ({ data }) => {
    const { text, icon, bankAccountInfo, autoDismiss = true, userId, metaData = {} } = data as WebNotificationMessage

    if (userId && loggedInUserId !== userId) {
      return
    }

    if (isMeinBuero() && isMobile && metaData.type === 'ONBOARDING_SUCCESS') {
      return
    }

    dispatch(prependNews(data))
    toast.showToast({ type: 'info', message: text, icon, key: undefined, bankAccountInfo, autoDismiss })
  })

  // Notify Pendo about route changes in SPA, even when the agent becomes ready after navigation.
  useEffect(() => {
    const cancelPendingPendoPageLoad = () => {
      if (pendoPageLoadRafRef.current !== null) {
        window.cancelAnimationFrame(pendoPageLoadRafRef.current)
        pendoPageLoadRafRef.current = null
      }
      if (pendoPageLoadTimeoutRef.current !== null) {
        window.clearTimeout(pendoPageLoadTimeoutRef.current)
        pendoPageLoadTimeoutRef.current = null
      }
    }

    const schedulePendoPageLoad = () => {
      cancelPendingPendoPageLoad()
      pendoPageLoadRafRef.current = window.requestAnimationFrame(() => {
        pendoPageLoadRafRef.current = null
        pendoPageLoadTimeoutRef.current = window.setTimeout(() => {
          pendoPageLoadTimeoutRef.current = null
          if (!window.pendo?.isReady?.()) return
          window.pendo.pageLoad()
        }, PENDO_PAGELOAD_DELAY_MS)
      })
    }

    const trySchedulePendoPageLoad = () => {
      if (!window.pendo?.isReady?.()) {
        return false
      }

      // Run pageLoad after the next paint and a short delay to avoid guide-step teardown races on route transitions.
      schedulePendoPageLoad()
      return true
    }

    cancelPendingPendoPageLoad()

    if (trySchedulePendoPageLoad()) {
      return cancelPendingPendoPageLoad
    }

    const onPendoReady = () => {
      trySchedulePendoPageLoad()
    }

    window.addEventListener(PENDO_READY_EVENT, onPendoReady)
    window.addEventListener(PENDO_SCRIPT_LOADED_EVENT, onPendoReady)

    return () => {
      window.removeEventListener(PENDO_READY_EVENT, onPendoReady)
      window.removeEventListener(PENDO_SCRIPT_LOADED_EVENT, onPendoReady)
      cancelPendingPendoPageLoad()
    }
  }, [location.pathname])

  const innerContent = (
    <GlobalPortalProvider>
      <MenuPopoverProvider>
        <Outlet />
        <OnboardingGuard />
        <TenantChanged user={user} />
        <ToastList />
        <Offline />
        <div id="globalElement"></div>
        <div id="drawerEl"></div>
        <div id="modalEl"></div>
        <div id="tanModalEl"></div>
        <div id="pinModalEl"></div>
        <div id="universalModalEl"></div>
        <div id="redirectModalEl"></div>
        <div id="validationModalEl"></div>
        <ModalContainer />
      </MenuPopoverProvider>
    </GlobalPortalProvider>
  )

  return (
    <React.Suspense fallback={<Loader />}>
      {innerContent}
    </React.Suspense>
  )
}

const TenantChanged = (props) => {
  const { user } = props
  const { activeTenant: { tenantId } } = useSelector(state => state.user.info)
  const openModal = useOpenModal()
  const checkPbb = () => {
    if (!tenantId) return
    const jwtParts = user?.token?.split('.')
    if (jwtParts?.length < 2) return

    let bankingLogsEnabled = false
    try {
      const decodedPayload = atob(jwtParts[1])
      const parsedPayload = JSON.parse(decodedPayload)
      const payload = { bankingLogsEnabled: false, ...parsedPayload }
      bankingLogsEnabled = payload.bankingLogsEnabled
    } catch (error) {
      console.error(error)
    }

    if (!bankingLogsEnabled) return

    openModal(close => ({
      component: 'ModalBig',
      props: {
        disableEscapeKeyDown: true,
        disableBackdropClick: true,
        title: lang.pbbLog.modalTitle,
        actions: [
          <div key='closeButton' style={{ marginBottom: -20 + 'px' }}>
            <ButtonSquare
              onClick={close}
              label={lang.closeButtonLabel}
              variant={VARIANTS.small}
              dataQsId='pbb-log-modal-close' >
              {lang.pbbLog.modalButton}
            </ButtonSquare>
          </div>,
        ],
      },
      children: [
        <div key='checkPbbLog'>
          <p style={{ marginTop: 0, marginBottom: 30 + 'px' }}>{lang.pbbLog.modalContent}</p>
        </div>,
      ],
    }))
  }

  useEffect(() => {
    checkPbb()
  }, [tenantId])

  return <></>
}

export default App
